import React, { useState, useEffect } from "react";
import {
  Brain,
  CheckCircle,
  Clock,
  Sparkles,
  BookOpen,
  ArrowRight,
  TrendingUp,
  Award,
  Video,
  Play,
  RotateCcw,
  Users,
  Compass,
  Zap,
  Box,
  Layers,
  Edit3,
  Calendar,
  Download,
  AlertCircle,
  HelpCircle,
  Lock,
  Percent,
  X,
  Plus,
  Trash2,
  PhoneCall,
  ChevronRight,
  TrendingDown,
  Gift,
  Share2,
  BarChart2
} from "lucide-react";
import { toast, Toaster } from "sonner";

// Initial realistic statistics to seed the analytics dashboard
const INITIAL_STATS = {
  visits: 342,
  couponApplied: 0,
  leadsCount: 4,
  purchasesCount: 2,
  revenue: 4498
};

const INITIAL_LEADS = [
  {
    id: "lead-1",
    name: "Aarav Mehta",
    email: "aarav.mehta@gmail.com",
    whatsapp: "+91 98123 45678",
    interest: "Rubik's Cube Training (Beginner to Advanced)",
    audience: "Student",
    goals: "Logic & Problem Solving",
    date: "2026-02-25",
    purchased: true,
    packageName: "Cognitive Starter Masterclass",
    amount: 499,
    timestamp: Date.now() - 172800000
  },
  {
    id: "lead-2",
    name: "Priyanjali Sen",
    email: "priya.sen@outlook.com",
    whatsapp: "+91 98234 56789",
    interest: "Finger Brain Graphy & Scientific Handwriting",
    audience: "Parent",
    goals: "Handwriting & Personality Correction",
    date: "2026-02-26",
    purchased: true,
    packageName: "30-Day Brain & Mind Style Bootcamp",
    amount: 3999,
    timestamp: Date.now() - 86400000
  },
  {
    id: "lead-3",
    name: "David Miller",
    email: "david.miller@yahoo.com",
    whatsapp: "+1 415 555 1289",
    interest: "Juggling 3 Balls (Neuro-Coordination)",
    audience: "Working Professional",
    goals: "Focus & Left-Right Brain Balance",
    date: "2026-02-26",
    purchased: false,
    packageName: "",
    amount: 0,
    timestamp: Date.now() - 43200000
  },
  {
    id: "lead-4",
    name: "Sunita Deshmukh",
    email: "sunita.d@rediffmail.com",
    whatsapp: "+91 91234 98765",
    interest: "Personality Development & Mind Style Coaching",
    audience: "Homemaker",
    goals: "Confidence & Mind Style Coaching",
    date: "2026-02-27",
    purchased: false,
    packageName: "",
    amount: 0,
    timestamp: Date.now() - 10800000
  }
];

export default function App() {
  // Funnel stage tracking (Step 1: Landing, 2: Quiz/Registration, 3: Offers, 4: Thank-You/Confirmation Hub)
  const [activeStep, setActiveStep] = useState(1);
  const [showDashboard, setShowDashboard] = useState(false);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  
  // Quiz & Lead state
  const [leadName, setLeadName] = useState("");
  const [leadEmail, setLeadEmail] = useState("");
  const [leadWhatsapp, setLeadWhatsapp] = useState("");
  const [selectedInterest, setSelectedInterest] = useState("Rubik's Cube Training (Beginner to Advanced)");
  const [selectedAudience, setSelectedAudience] = useState("");
  const [selectedGoal, setSelectedGoal] = useState("");
  const [leadConsent, setLeadConsent] = useState(true);
  const [quizStep, setQuizStep] = useState(1); // sub-steps for the registration quiz

  // Price & Coupon code state
  const [couponCode, setCouponCode] = useState("");
  const [discountPercent, setDiscountPercent] = useState(0);
  const [appliedCouponName, setAppliedCouponName] = useState("");

  // Countdown timer state for special offer (minutes & seconds)
  const [timerMinutes, setTimerMinutes] = useState(14);
  const [timerSeconds, setTimerSeconds] = useState(52);

  // Selected package for simulated checkout
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("upi");
  const [simulatedUpiId, setSimulatedUpiId] = useState("");
  const [simulatedCardNumber, setSimulatedCardNumber] = useState("");
  
  // Analytics Data state (loaded from local storage or seeded)
  const [stats, setStats] = useState(INITIAL_STATS);
  const [leads, setLeads] = useState(INITIAL_LEADS);

  // Load stats from localStorage
  useEffect(() => {
    const savedStats = localStorage.getItem("bsg_stats");
    const savedLeads = localStorage.getItem("bsg_leads");

    if (savedStats) {
      setStats(JSON.parse(savedStats));
    } else {
      localStorage.setItem("bsg_stats", JSON.stringify(INITIAL_STATS));
    }

    if (savedLeads) {
      setLeads(JSON.parse(savedLeads));
    } else {
      localStorage.setItem("bsg_leads", JSON.stringify(INITIAL_LEADS));
    }

    // Record landing page visit on initial load
    incrementStat("visits");
  }, []);

  // Sync state helper to update localStorage
  const incrementStat = (key, valueToAdd = 1) => {
    setStats((prev) => {
      const updated = { ...prev, [key]: prev[key] + valueToAdd };
      localStorage.setItem("bsg_stats", JSON.stringify(updated));
      return updated;
    });
  };

  // Special countdown timer simulation
  useEffect(() => {
    const timer = setInterval(() => {
      if (timerSeconds > 0) {
        setTimerSeconds(timerSeconds - 1);
      } else if (timerSeconds === 0) {
        if (timerMinutes === 0) {
          // Reset countdown timer to simulate continuous urgency
          setTimerMinutes(15);
          setTimerSeconds(0);
        } else {
          setTimerMinutes(timerMinutes - 1);
          setTimerSeconds(59);
        }
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [timerSeconds, timerMinutes]);

  // Lead capture action
  const handleLeadSubmit = (e) => {
    e.preventDefault();
    if (!leadName || !leadEmail || !leadWhatsapp || !selectedAudience || !selectedGoal) {
      toast.error("Please fill in all diagnostic and contact details.");
      return;
    }

    const newLead = {
      id: `lead-${Date.now()}`,
      name: leadName,
      email: leadEmail,
      whatsapp: leadWhatsapp,
      interest: selectedInterest,
      audience: selectedAudience,
      goals: selectedGoal,
      date: new Date().toISOString().split("T")[0],
      purchased: false,
      packageName: "",
      amount: 0,
      timestamp: Date.now()
    };

    const updatedLeads = [newLead, ...leads];
    setLeads(updatedLeads);
    localStorage.setItem("bsg_leads", JSON.stringify(updatedLeads));

    incrementStat("leadsCount");
    toast.success("Demonstration registration confirmed!");
    
    // Auto progress to the Core paid offer page (Step 3) to initiate high-converting upsell
    setActiveStep(3);
  };

  // Apply simulated Coupon code
  const handleApplyCoupon = () => {
    const code = couponCode.trim().toUpperCase();
    if (code === "HAPPINESS" || code === "BRAIN") {
      setDiscountPercent(15);
      setAppliedCouponName(code);
      incrementStat("couponApplied");
      toast.success(`Coupon "${code}" applied! Additional 15% discount loaded.`);
    } else if (code) {
      toast.error("Invalid coupon code. Try code: HAPPINESS");
    }
  };

  // Checkout submission action
  const handleCompleteCheckout = () => {
    if (paymentMethod === "upi" && !simulatedUpiId) {
      toast.error("Please enter a simulated UPI ID");
      return;
    }
    if (paymentMethod === "card" && !simulatedCardNumber) {
      toast.error("Please enter a simulated card number");
      return;
    }

    const packagePrice = selectedPackage.price;
    const finalAmount = Math.round(packagePrice * (1 - discountPercent / 100));

    // Update lead in state as a buyer
    const updatedLeads = leads.map((lead) => {
      // If we don't have current lead, we assume a walk-in purchaser or update the latest lead we added
      if (lead.email === leadEmail || (leads.length > 0 && lead.id === leads[0].id)) {
        return {
          ...lead,
          purchased: true,
          packageName: selectedPackage.title,
          amount: finalAmount
        };
      }
      return lead;
    });

    setLeads(updatedLeads);
    localStorage.setItem("bsg_leads", JSON.stringify(updatedLeads));

    incrementStat("purchasesCount");
    incrementStat("revenue", finalAmount);

    toast.success("Payment simulated successfully! Welcome to Brain Sttudio Global!");
    setShowCheckoutModal(false);
    setActiveStep(4); // Thank-you/Confirmation Hub page
  };

  const handleResetData = () => {
    localStorage.removeItem("bsg_stats");
    localStorage.removeItem("bsg_leads");
    setStats({
      visits: 1,
      couponApplied: 0,
      leadsCount: 0,
      purchasesCount: 0,
      revenue: 0
    });
    setLeads([]);
    toast.success("Analytics simulation data reset successfully!");
  };

  const handleAddMockLeads = () => {
    localStorage.setItem("bsg_stats", JSON.stringify(INITIAL_STATS));
    localStorage.setItem("bsg_leads", JSON.stringify(INITIAL_LEADS));
    setStats(INITIAL_STATS);
    setLeads(INITIAL_LEADS);
    toast.success("Mock statistical data injected!");
  };

  // Programs details list mapped with icons & assets
  const programs = [
    {
      title: "Rubik’s Cube Training",
      category: "Logical Thinking",
      desc: "Learn from beginner (3x3 layer-by-layer) to advanced speed-solving techniques (CFOP Method, Pyraminx, Megaminx, and blindfold solving).",
      icon: <Box className="w-10 h-10 text-amber-500" />,
      tag: "Best Seller",
      image: "https://images.unsplash.com/photo-1590845947667-381579052389?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=600",
      stats: "5,000+ Students trained worldwide"
    },
    {
      title: "Pyraminx Puzzle Training",
      category: "Pattern Recognition",
      desc: "Master the unique pyramid-shaped mechanical twisty puzzle. Boost spatial logic, tactile reflexes, and high-speed algorithm memory.",
      icon: <Zap className="w-10 h-10 text-orange-500" />,
      tag: "Reflex Booster",
      image: "https://images.unsplash.com/photo-1758598737977-094cb3ca9ce8?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=600",
      stats: "Excellent for kids aged 6+"
    },
    {
      title: "Juggling 3 Balls",
      category: "Neuro-Coordination",
      desc: "Synchronize your left and right brain hemispheres. Develop elite hand-eye coordination, reaction speeds, and peripheral focus.",
      icon: <Compass className="w-10 h-10 text-cyan-500" />,
      tag: "Mindfulness",
      image: "https://images.unsplash.com/photo-1554564200-198b0cd87cf5?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=600",
      stats: "Used in corporate stress-buster sessions"
    },
    {
      title: "Finger Brain Graphy & Handwriting",
      category: "Fine Motor Analytics",
      desc: "Scientific handwriting analysis combined with physical finger gymnastics to stimulate neural connections, focus, and personality alignment.",
      icon: <Edit3 className="w-10 h-10 text-purple-500" />,
      tag: "Scientific",
      image: "https://images.unsplash.com/photo-1657474038822-b996a018e268?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=600",
      stats: "Reveals 20+ core personality characteristics"
    },
    {
      title: "Speed Sport Stacking Cups",
      category: "Reflex & Coordination",
      desc: "Stack and unstack 12 specially designed sport cups in pre-determined sequences at lightning speeds to maximize ambidextrous motor skill.",
      icon: <Layers className="w-10 h-10 text-emerald-500" />,
      tag: "Super Fun",
      image: "https://images.unsplash.com/photo-1565499110650-e298c6be8c01?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=600",
      stats: "Improves reaction time by up to 30%"
    },
    {
      title: "Personality & Mind Coaching",
      category: "Mind Style Mastery",
      desc: "Coaching by founder Sreenivas Murty K. Understand your emotional blocks, break self-limiting patterns, and craft an unshakeable mind style.",
      icon: <Brain className="w-10 h-10 text-pink-500" />,
      tag: "Premium",
      image: "https://images.unsplash.com/photo-1722623256430-c4fb848dcaaa?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=600",
      stats: "Designed for professionals and homemakers"
    }
  ];

  // Core Paid Packages
  const packages = [
    {
      id: "pkg-starter",
      title: "Cognitive Starter Masterclass",
      originalPrice: 2999,
      price: 499,
      duration: "3 Live Interactive Sessions",
      badge: "Introductory Value",
      color: "border-slate-800 hover:border-amber-500/40",
      accentBg: "bg-amber-500/10",
      accentText: "text-amber-500",
      features: [
        "Interactive 3x3 Rubik's Cube fundamental solves",
        "Introduction to Scientific Handwriting analysis",
        "3 Basic Neuro-Coordination Finger Gym exercises",
        "Direct Q&A session with founder Sreenivas Murty K",
        "Downloadable BSG Digital Starter Playbook PDF"
      ]
    },
    {
      id: "pkg-bootcamp",
      title: "30-Day Brain Happiness Bootcamp",
      originalPrice: 14999,
      price: 3999,
      duration: "30-Day Core Transformation Program",
      badge: "Most Popular & High Conversion",
      isPopular: true,
      color: "border-amber-500 shadow-[0_0_30px_rgba(245,158,11,0.15)] hover:border-amber-400",
      accentBg: "bg-gradient-to-r from-amber-500 to-orange-500",
      accentText: "text-white",
      features: [
        "Master both 3x3 Rubik's Cube & Pyraminx Pyramid puzzles",
        "Full 3-Ball Juggling cascading pattern coaching",
        "Speed Sport Stacking Cups complete tournament layout",
        "Finger Brain Graphy correction & Handwriting enhancement",
        "Weekly live group Mind Style Coaching sessions",
        "Official Certificate of Brain Transformation"
      ]
    },
    {
      id: "pkg-vip",
      title: "VIP 1-on-1 Mind Style Mentorship",
      originalPrice: 49999,
      price: 19999,
      duration: "3 Months Personalized Coaching",
      badge: "Exclusive Access",
      color: "border-slate-800 hover:border-purple-500/40",
      accentBg: "bg-purple-500/10",
      accentText: "text-purple-500",
      features: [
        "100% personalized 1-on-1 calls with Sreenivas Murty K",
        "Indepth Handwriting & Personality Assessment Report",
        "Tailored brain-training syllabus for your exact lifestyle",
        "Direct continuous WhatsApp accountability with Sreenivas",
        "A holistic Mind style reprogramming map (family/business)",
        "Lifetime access to all future group masterclasses"
      ]
    }
  ];

  // Calculated Conversion Rates
  const calcLeadsRate = stats.visits > 0 ? ((stats.leadsCount / stats.visits) * 100).toFixed(1) : 0;
  const calcPurchaseRate = stats.leadsCount > 0 ? ((stats.purchasesCount / stats.leadsCount) * 100).toFixed(1) : 0;
  const calcOverallRate = stats.visits > 0 ? ((stats.purchasesCount / stats.visits) * 100).toFixed(1) : 0;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col justify-between selection:bg-amber-500 selection:text-slate-950 relative overflow-hidden">
      <Toaster position="top-right" richColors />

      {/* Background Decorative Blobs */}
      <div className="absolute top-[-10%] left-[-10%] w-[500px] h-[500px] rounded-full bg-amber-500/5 blur-[120px] pointer-events-none" />
      <div className="absolute bottom-[-10%] right-[-10%] w-[500px] h-[500px] rounded-full bg-cyan-500/5 blur-[120px] pointer-events-none" />

      {/* HEADER Landmarks & Logo */}
      <header className="border-b border-slate-900 bg-slate-950/80 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => setActiveStep(1)} data-testid="header-logo-container">
            <img 
              src="https://tagmango.com/assets/bsg-logo-black-text-54297b29f2f0e6a87f838d7ace724844.png" 
              alt="Brain Sttudio Global Logo" 
              className="h-12 w-auto object-contain bg-white rounded-lg p-1 border border-white/20 shadow-md shadow-white/5"
            />
          </div>

          <nav className="hidden md:flex items-center gap-6 text-sm font-medium text-slate-400">
            <button 
              onClick={() => setActiveStep(1)} 
              className={`hover:text-amber-400 transition-colors ${activeStep === 1 ? "text-amber-400 font-bold" : ""}`}
              data-testid="nav-link-home"
            >
              Programs
            </button>
            <button 
              onClick={() => { setActiveStep(2); setQuizStep(1); }} 
              className={`hover:text-amber-400 transition-colors ${activeStep === 2 ? "text-amber-400 font-bold" : ""}`}
              data-testid="nav-link-register"
            >
              Free Assessment
            </button>
            <button 
              onClick={() => setActiveStep(3)} 
              className={`hover:text-amber-400 transition-colors ${activeStep === 3 ? "text-amber-400 font-bold" : ""}`}
              data-testid="nav-link-offers"
            >
              Premium Courses
            </button>
          </nav>

          <div className="flex items-center gap-3">
            {/* Live Analytics Toggle button */}
            <button
              onClick={() => setShowDashboard(!showDashboard)}
              className="px-4 py-2 text-xs font-semibold rounded-lg bg-slate-900 border border-slate-800 text-slate-300 hover:text-white hover:bg-slate-800 hover:border-slate-700 flex items-center gap-2 transition-all"
              data-testid="dashboard-toggle-button"
            >
              <BarChart2 className="w-4 h-4 text-cyan-400" />
              <span className="hidden sm:inline">Mango AI Live Stats</span>
            </button>

            <button
              onClick={() => {
                setActiveStep(2);
                setQuizStep(1);
              }}
              className="px-5 py-2.5 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 text-slate-950 font-bold text-xs sm:text-sm hover:from-amber-400 hover:to-orange-400 shadow-md shadow-amber-500/10 hover:shadow-amber-500/20 active:scale-95 transition-all flex items-center gap-1.5"
              data-testid="header-get-started-button"
            >
              <span>Get Free Demo</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      {/* MAIN CONTAINER */}
      <main className="flex-grow max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full">
        
        {/* FUNNEL STEP 1: LANDING & DISCOVERY */}
        {activeStep === 1 && (
          <div className="space-y-16 animate-fade-in" data-testid="funnel-step-landing">
            
            {/* Hero Section */}
            <section className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center pt-4 sm:pt-8">
              <div className="lg:col-span-7 space-y-6">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/20 text-xs font-semibold text-amber-400">
                  <Sparkles className="w-3.5 h-3.5" />
                  <span>Join Sreenivas Murty K’s Signature Academy</span>
                </div>
                
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-tight">
                  Train the <span className="bg-gradient-to-r from-amber-400 via-orange-500 to-cyan-400 bg-clip-text text-transparent">Brain</span>.<br />
                  Understand the <span className="bg-gradient-to-r from-cyan-400 to-purple-500 bg-clip-text text-transparent">Mind</span>.<br />
                  Transform <span className="text-slate-100">Personality</span>.
                </h1>
                
                <p className="text-base sm:text-lg text-slate-400 max-w-2xl leading-relaxed">
                  Brain Sttudio Global is a premium cognitive training hub designed to supercharge intelligence, memory, coordination, and mindfulness for age groups 6 to 60+ worldwide.
                </p>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 pt-2">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-amber-500 shrink-0" />
                    <span className="text-sm font-medium text-slate-300">No Prior Skills Needed</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-orange-500 shrink-0" />
                    <span className="text-sm font-medium text-slate-300">Live 1-on-1 Feedback</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-cyan-500 shrink-0" />
                    <span className="text-sm font-medium text-slate-300">Global Certification</span>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  <button
                    onClick={() => {
                      setActiveStep(2);
                      setQuizStep(1);
                    }}
                    className="px-8 py-4 rounded-xl bg-gradient-to-r from-amber-500 via-orange-500 to-orange-600 text-slate-950 font-bold text-base hover:from-amber-400 hover:to-orange-500 shadow-xl shadow-amber-500/10 hover:shadow-amber-500/25 active:scale-98 transition-all flex items-center justify-center gap-2"
                    data-testid="hero-primary-cta"
                  >
                    <span>Claim Free Beginner Demo Kit</span>
                    <ArrowRight className="w-5 h-5 stroke-[2.5]" />
                  </button>
                  
                  <button
                    onClick={() => setIsVideoModalOpen(true)}
                    className="px-8 py-4 rounded-xl bg-slate-900 hover:bg-slate-850 border border-slate-800 hover:border-slate-700 text-slate-100 font-bold text-base transition-all flex items-center justify-center gap-2"
                    data-testid="hero-secondary-cta"
                  >
                    <Play className="w-5 h-5 text-amber-500 fill-amber-500" />
                    <span>Watch Founder Introduction</span>
                  </button>
                </div>
              </div>

              {/* Bento Grid Visual Element */}
              <div className="lg:col-span-5 relative">
                <div className="relative rounded-2xl overflow-hidden border border-slate-800 bg-slate-900 p-4 shadow-2xl shadow-amber-500/5 group hover:border-slate-700 transition-all">
                  <div className="aspect-[4/3] rounded-xl overflow-hidden relative">
                    <img 
                      src="https://images.unsplash.com/photo-1590845947667-381579052389?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwxfHxydWJpayUyMGN1YmUlMjBzb2x2aW5nJTIwY29sb3JmdWx8ZW58MHx8fHllbGxvd3wxNzkyMDY4MDIzfDA&ixlib=rb-4.1.0&q=85&w=600" 
                      alt="Student practicing Rubik's Cube training at Brain Sttudio"
                      className="w-full h-full object-cover transform scale-105 group-hover:scale-100 duration-500" 
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/20 to-transparent flex items-end p-6">
                      <div>
                        <span className="text-xs font-bold text-amber-400 uppercase tracking-widest bg-amber-500/10 px-2 py-0.5 rounded border border-amber-500/20">Featured Training</span>
                        <h4 className="text-lg font-bold text-slate-100 mt-1">Rubik's Cube Masterclass</h4>
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3 mt-4">
                    <div className="p-3 bg-slate-950/50 rounded-xl border border-slate-900 flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-cyan-500/10 flex items-center justify-center shrink-0">
                        <Users className="w-5 h-5 text-cyan-400" />
                      </div>
                      <div>
                        <span className="text-xs text-slate-400 block">Worldwide Users</span>
                        <span className="text-sm font-bold text-slate-200">12,000+</span>
                      </div>
                    </div>

                    <div className="p-3 bg-slate-950/50 rounded-xl border border-slate-900 flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center shrink-0">
                        <Award className="w-5 h-5 text-purple-400" />
                      </div>
                      <div>
                        <span className="text-xs text-slate-400 block">Satisfaction</span>
                        <span className="text-sm font-bold text-slate-200">99.4% Rating</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Core Programs Showcase */}
            <section className="space-y-8" id="programs-section">
              <div className="text-center space-y-4 max-w-3xl mx-auto">
                <span className="text-xs font-bold text-cyan-400 uppercase tracking-widest">Syllabus Overview</span>
                <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Our Core Transformation Programs</h2>
                <p className="text-slate-400">
                  Carefully designed curriculums targeting fine motor coordination, mathematical brain logic, intuitive handwriting insights, and emotional wellness.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {programs.map((prog, index) => (
                  <article 
                    key={index}
                    className="rounded-2xl border border-slate-800 bg-slate-900/40 hover:bg-slate-900 hover:border-slate-700 transition-all duration-300 p-6 flex flex-col justify-between group hover:shadow-[0_0_30px_rgba(245,158,11,0.05)]"
                    data-testid={`program-card-${index}`}
                  >
                    <div className="space-y-4">
                      <div className="flex justify-between items-start">
                        <div className="p-3 bg-slate-950 rounded-xl border border-slate-800 group-hover:border-slate-700 transition-colors">
                          {prog.icon}
                        </div>
                        <span className="text-[10px] font-bold tracking-widest uppercase bg-slate-800/80 px-2 py-0.5 rounded-md border border-slate-700/50 text-slate-300">
                          {prog.tag}
                        </span>
                      </div>
                      
                      <div className="space-y-2">
                        <span className="text-xs font-medium text-amber-500 uppercase tracking-wider">{prog.category}</span>
                        <h3 className="text-lg font-bold text-slate-100 group-hover:text-amber-400 transition-colors">{prog.title}</h3>
                        <p className="text-sm text-slate-400 leading-relaxed">{prog.desc}</p>
                      </div>
                    </div>

                    <div className="border-t border-slate-800/50 mt-6 pt-4 flex items-center justify-between">
                      <span className="text-xs text-slate-500 font-medium">{prog.stats}</span>
                      <button
                        onClick={() => {
                          setSelectedInterest(prog.title);
                          setActiveStep(2);
                          setQuizStep(1);
                        }}
                        className="text-xs font-bold text-amber-400 group-hover:text-amber-300 flex items-center gap-1"
                        data-testid={`program-join-btn-${index}`}
                      >
                        <span>Request Demo</span>
                        <ChevronRight className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            </section>

            {/* Founder Spotlight: Sreenivas Murty K */}
            <section className="rounded-3xl border border-slate-800 bg-slate-900/30 p-8 sm:p-12 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-[300px] h-[300px] bg-purple-500/5 rounded-full blur-[80px] pointer-events-none" />
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                <div className="lg:col-span-4 flex justify-center">
                  <div className="relative group">
                    <div className="absolute -inset-1.5 rounded-3xl bg-gradient-to-r from-amber-500 to-purple-500 opacity-30 blur group-hover:opacity-40 transition duration-300" />
                    <div className="relative rounded-2xl overflow-hidden border border-slate-800 w-64 h-80 sm:w-72 sm:h-96">
                      <img 
                        src="https://images.unsplash.com/photo-1554564200-198b0cd87cf5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwzfHxqdWdnbGluZyUyMGN1YmUlMjBzcGVlZCUyMGN1cHMlMjB3b3Jrc2hvcCUyMGJyYWluJTIwdHJhaW5pbmd8ZW58MHx8fHwxNzkyMDY4MDMxfDA&ixlib=rb-4.1.0&q=85&w=600" 
                        alt="Sreenivas Murty K., founder of Brain Sttudio Global conducting workshop" 
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                </div>

                <div className="lg:col-span-8 space-y-6">
                  <div className="space-y-2">
                    <span className="text-xs font-bold text-purple-400 uppercase tracking-widest">MEET THE FOUNDER & CHIEF COACH</span>
                    <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-100">
                      Sreenivas Murty K.
                    </h2>
                    <p className="text-sm font-semibold text-slate-400 italic">
                      Brain Trainer, Hand-Eye Coordination Coach & Mind Style Consultant
                    </p>
                  </div>

                  <p className="text-slate-300 leading-relaxed">
                    Sreenivas Murty K. established **Brain Sttudio Global — The Real Happiness Factory** with a core mandate: to synthesize fun educational toys with serious cognitive development methods. 
                  </p>
                  <p className="text-slate-400 text-sm leading-relaxed">
                    By combining motor exercises like Speed Cup Stacking and Juggling with algorithmic Rubik's Cube training and Scientific Handwriting Graphy, Sreenivas has successfully trained corporate leaders, children, students, and seniors worldwide to overcome stress, build left/right brain ambidexterity, and boost self-esteem.
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                    <div className="p-4 bg-slate-950/40 rounded-xl border border-slate-850">
                      <span className="text-2xl font-black text-amber-500 block">15,000+</span>
                      <span className="text-xs text-slate-400 font-medium">Individuals Personally Coached</span>
                    </div>
                    <div className="p-4 bg-slate-950/40 rounded-xl border border-slate-850">
                      <span className="text-2xl font-black text-purple-400 block">10+ Years</span>
                      <span className="text-xs text-slate-400 font-medium">Of Deep Behavioral & Skill Research</span>
                    </div>
                  </div>

                  <button
                    onClick={() => {
                      setActiveStep(2);
                      setQuizStep(1);
                    }}
                    className="px-6 py-3 rounded-lg bg-slate-900 hover:bg-slate-850 border border-slate-800 hover:border-slate-700 text-slate-200 text-sm font-bold transition-all flex items-center gap-1.5"
                    data-testid="founder-cta-button"
                  >
                    <span>Request Free 1-on-1 Consultation Call</span>
                    <PhoneCall className="w-4 h-4 text-amber-500" />
                  </button>
                </div>
              </div>
            </section>

            {/* Testimonials or Program Proof */}
            <section className="space-y-8">
              <div className="text-center max-w-3xl mx-auto space-y-2">
                <span className="text-xs font-bold text-amber-400 uppercase tracking-widest">Real Transformations</span>
                <h2 className="text-3xl font-extrabold tracking-tight text-slate-100">Verified Parent & Student Stories</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="p-6 rounded-2xl border border-slate-900 bg-slate-900/20 space-y-4">
                  <div className="flex gap-1 text-amber-400 text-sm">★★★★★</div>
                  <p className="text-sm text-slate-300 leading-relaxed">
                    "My 8-year-old was struggling with focus. After 2 months of Rubik's cube and Juggling coaching at Brain Sttudio, his concentration Span improved dramatically. Highly recommended!"
                  </p>
                  <div>
                    <span className="text-xs font-bold text-slate-200 block">Anjali Sharma</span>
                    <span className="text-[10px] text-slate-500 block font-medium">Mother of Aarav (Age 8) • Bangalore</span>
                  </div>
                </div>

                <div className="p-6 rounded-2xl border border-slate-900 bg-slate-900/20 space-y-4">
                  <div className="flex gap-1 text-amber-400 text-sm">★★★★★</div>
                  <p className="text-sm text-slate-300 leading-relaxed">
                    "The Scientific Handwriting Graphy course is phenomenal. The coach evaluated my handwriting, suggested 3 specific letter shape corrections, and it boosted my academic productivity!"
                  </p>
                  <div>
                    <span className="text-xs font-bold text-slate-200 block">Karthik R.</span>
                    <span className="text-[10px] text-slate-500 block font-medium">Engineering Student • Chennai</span>
                  </div>
                </div>

                <div className="p-6 rounded-2xl border border-slate-900 bg-slate-900/20 space-y-4">
                  <div className="flex gap-1 text-amber-400 text-sm">★★★★★</div>
                  <p className="text-sm text-slate-300 leading-relaxed">
                    "Being a corporate professional, I was looking for stress relief. Learning Pyraminx puzzle solving and Juggling has served as the best everyday therapeutic mental rest."
                  </p>
                  <div>
                    <span className="text-xs font-bold text-slate-200 block">Deepak Sen</span>
                    <span className="text-[10px] text-slate-500 block font-medium">Senior Software Architect • Hyderabad</span>
                  </div>
                </div>
              </div>
            </section>

            {/* Quick trust metrics banner */}
            <section className="py-8 border-y border-slate-900 bg-slate-950 flex flex-wrap justify-around items-center gap-6 text-center">
              <div>
                <span className="text-3xl font-extrabold text-amber-500 block">100%</span>
                <span className="text-xs text-slate-400">Risk-Free Satisfaction</span>
              </div>
              <div className="h-8 w-px bg-slate-900 hidden md:block" />
              <div>
                <span className="text-3xl font-extrabold text-orange-500 block">6+</span>
                <span className="text-xs text-slate-400">Specialized Cognitive Arts</span>
              </div>
              <div className="h-8 w-px bg-slate-900 hidden md:block" />
              <div>
                <span className="text-3xl font-extrabold text-cyan-500 block">12,000+</span>
                <span className="text-xs text-slate-400">Community Members</span>
              </div>
              <div className="h-8 w-px bg-slate-900 hidden md:block" />
              <div>
                <span className="text-3xl font-extrabold text-purple-400 block">Secure</span>
                <span className="text-xs text-slate-400">SSL Encrypted Checkout</span>
              </div>
            </section>
          </div>
        )}

        {/* FUNNEL STEP 2: MULTI-STEP LEAD CAPTURE & DIAGNOSTIC QUIZ */}
        {activeStep === 2 && (
          <div className="max-w-xl mx-auto py-4 sm:py-8 animate-fade-in" data-testid="funnel-step-quiz">
            <div className="rounded-2xl border border-slate-800 bg-slate-900 p-6 sm:p-8 space-y-6 shadow-2xl relative overflow-hidden">
              <div className="absolute top-0 left-0 h-1.5 bg-gradient-to-r from-amber-500 to-orange-500" style={{ width: `${(quizStep / 3) * 100}%` }} />
              
              <div className="flex justify-between items-center text-xs font-semibold text-slate-400">
                <span>Free Evaluation Quiz</span>
                <span>Step {quizStep} of 3</span>
              </div>

              {quizStep === 1 && (
                <div className="space-y-6 animate-slide-up">
                  <div className="space-y-2">
                    <h3 className="text-xl sm:text-2xl font-bold text-slate-100">Who is registering for the cognitive demo?</h3>
                    <p className="text-sm text-slate-400">This helps us personalize Sreenivas Murty K.'s introductory session specifically for your age and requirement.</p>
                  </div>

                  <div className="space-y-3">
                    {[
                      { id: "Child/Student", label: "My Child (Aged 6 - 15 years old)", desc: "Build focus, logical thinking, and motor coordination" },
                      { id: "Working Professional", label: "Working Professional (18+ years old)", desc: "Reduce stress, improve concentration, left/right brain balance" },
                      { id: "Homemaker", label: "Homemaker / Parent", desc: "Enjoyable cognitive hobbies, mindfulness and neuro-coordination" },
                      { id: "Senior Learner", label: "Senior Citizen (60+ years old)", desc: "Active memory retention, neuroplasticity exercises" },
                      { id: "Educator", label: "Teacher / Educational Coach", desc: "Learn to teach cognitive puzzles, handwriting graphy, and juggling" }
                    ].map((opt) => (
                      <button
                        key={opt.id}
                        onClick={() => {
                          setSelectedAudience(opt.id);
                          setQuizStep(2);
                        }}
                        className={`w-full text-left p-4 rounded-xl border transition-all flex justify-between items-center group ${selectedAudience === opt.id ? "bg-amber-500/10 border-amber-500 text-slate-100" : "bg-slate-950 border-slate-850 text-slate-300 hover:border-slate-700"}`}
                        data-testid={`quiz-audience-option-${opt.id.toLowerCase().replace("/", "-")}`}
                      >
                        <div>
                          <span className="font-semibold block text-sm group-hover:text-amber-400 transition-colors">{opt.label}</span>
                          <span className="text-[11px] text-slate-500 block mt-0.5">{opt.desc}</span>
                        </div>
                        <ChevronRight className={`w-4 h-4 transition-transform group-hover:translate-x-1 ${selectedAudience === opt.id ? "text-amber-500" : "text-slate-600"}`} />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {quizStep === 2 && (
                <div className="space-y-6 animate-slide-up">
                  <div className="space-y-2">
                    <h3 className="text-xl sm:text-2xl font-bold text-slate-100">Select your primary learning focus area</h3>
                    <p className="text-sm text-slate-400">Each cognitive art trains a distinct part of neural connections.</p>
                  </div>

                  <div className="space-y-3">
                    {[
                      { id: "Focus & Coordination", label: "Focus, Concentration & Patience", desc: "Perfect for Juggling, Speed Cup Stacking, and Pyraminx" },
                      { id: "Logic & Problem Solving", label: "Speed Rubik's Cube Solving & Algorithms", desc: "Best for building logical spatial reasoning and math skills" },
                      { id: "Handwriting & Personality Correction", label: "Finger Gymnastics & Scientific Handwriting", desc: "Great for neural connections and personality alignment" },
                      { id: "Stress Relief & Relaxation", label: "Mindfulness & Active De-stressing Hobbies", desc: "Designed for corporate professionals and seniors" }
                    ].map((opt) => (
                      <button
                        key={opt.id}
                        onClick={() => {
                          setSelectedGoal(opt.id);
                          setQuizStep(3);
                        }}
                        className={`w-full text-left p-4 rounded-xl border transition-all flex justify-between items-center group ${selectedGoal === opt.id ? "bg-orange-500/10 border-orange-500 text-slate-100" : "bg-slate-950 border-slate-850 text-slate-300 hover:border-slate-700"}`}
                        data-testid={`quiz-goal-option-${opt.id.toLowerCase().replace(/\s+/g, "-")}`}
                      >
                        <div>
                          <span className="font-semibold block text-sm group-hover:text-orange-400 transition-colors">{opt.label}</span>
                          <span className="text-[11px] text-slate-500 block mt-0.5">{opt.desc}</span>
                        </div>
                        <ChevronRight className={`w-4 h-4 transition-transform group-hover:translate-x-1 ${selectedGoal === opt.id ? "text-orange-500" : "text-slate-600"}`} />
                      </button>
                    ))}
                  </div>

                  <button
                    onClick={() => setQuizStep(1)}
                    className="text-xs font-semibold text-slate-400 hover:text-slate-200 flex items-center gap-1 mt-4"
                    data-testid="quiz-back-btn-1"
                  >
                    <RotateCcw className="w-3.5 h-3.5" />
                    <span>Back to previous question</span>
                  </button>
                </div>
              )}

              {quizStep === 3 && (
                <form onSubmit={handleLeadSubmit} className="space-y-5 animate-slide-up" data-testid="registration-form">
                  <div className="space-y-2">
                    <h3 className="text-xl sm:text-2xl font-bold text-slate-100">Submit Details & Get Access Code</h3>
                    <p className="text-sm text-slate-400">Complete registration to receive Sreenivas K's introductory PDFs and WhatsApp training reminders.</p>
                  </div>

                  <div className="space-y-4">
                    <div className="space-y-1.5">
                      <label htmlFor="lead-name" className="text-xs font-semibold text-slate-300 uppercase tracking-wider block">Your Full Name</label>
                      <input
                        id="lead-name"
                        type="text"
                        required
                        value={leadName}
                        onChange={(e) => setLeadName(e.target.value)}
                        placeholder="John Doe"
                        className="w-full px-4 py-3 rounded-lg bg-slate-950 border border-slate-800 text-slate-100 focus:outline-none focus:border-amber-500 text-sm"
                        data-testid="registration-form-name-input"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <label htmlFor="lead-email" className="text-xs font-semibold text-slate-300 uppercase tracking-wider block">Your Email Address</label>
                      <input
                        id="lead-email"
                        type="email"
                        required
                        value={leadEmail}
                        onChange={(e) => setLeadEmail(e.target.value)}
                        placeholder="john.doe@example.com"
                        className="w-full px-4 py-3 rounded-lg bg-slate-950 border border-slate-800 text-slate-100 focus:outline-none focus:border-amber-500 text-sm"
                        data-testid="registration-form-email-input"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <label htmlFor="lead-whatsapp" className="text-xs font-semibold text-slate-300 uppercase tracking-wider block">WhatsApp Number (For instant reminders)</label>
                      <input
                        id="lead-whatsapp"
                        type="tel"
                        required
                        value={leadWhatsapp}
                        onChange={(e) => setLeadWhatsapp(e.target.value)}
                        placeholder="+91 98765 43210"
                        className="w-full px-4 py-3 rounded-lg bg-slate-950 border border-slate-800 text-slate-100 focus:outline-none focus:border-amber-500 text-sm"
                        data-testid="registration-form-whatsapp-input"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <label htmlFor="lead-interest" className="text-xs font-semibold text-slate-300 uppercase tracking-wider block">Core Program of Interest</label>
                      <select
                        id="lead-interest"
                        value={selectedInterest}
                        onChange={(e) => setSelectedInterest(e.target.value)}
                        className="w-full px-4 py-3 rounded-lg bg-slate-950 border border-slate-800 text-slate-100 focus:outline-none focus:border-amber-500 text-sm"
                        data-testid="registration-form-interest-select"
                      >
                        <option value="Rubik's Cube Training (Beginner to Advanced)">Rubik's Cube Training</option>
                        <option value="Pyraminx Puzzle Masterclass">Pyraminx Puzzle Masterclass</option>
                        <option value="Juggling 3 Balls (Neuro-Coordination)">Juggling 3 Balls</option>
                        <option value="Finger Brain Graphy & Scientific Handwriting">Finger Brain Graphy & Scientific Handwriting</option>
                        <option value="Speed Sport Stacking Cups">Speed Sport Stacking Cups</option>
                        <option value="Personality Development & Mind Style Coaching">Personality Development & Coaching</option>
                      </select>
                    </div>

                    <div className="flex items-start gap-2.5 pt-1">
                      <input
                        id="lead-consent"
                        type="checkbox"
                        checked={leadConsent}
                        onChange={(e) => setLeadConsent(e.target.checked)}
                        className="mt-1 h-4 w-4 rounded border-slate-800 text-amber-500 focus:ring-amber-500/20 bg-slate-950"
                        data-testid="registration-form-consent-checkbox"
                      />
                      <label htmlFor="lead-consent" className="text-xs text-slate-400 select-none leading-relaxed">
                        I consent to receive learning PDFs, WhatsApp training reminders, and relevant high-performance course updates from Sreenivas Murty K. We never spam.
                      </label>
                    </div>
                  </div>

                  <button
                    type="submit"
                    className="w-full py-4 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-slate-950 font-bold text-base hover:from-amber-400 hover:to-orange-400 shadow-xl shadow-amber-500/10 active:scale-98 transition-all flex items-center justify-center gap-2"
                    data-testid="registration-form-submit-button"
                  >
                    <span>Confirm Free Registration</span>
                    <ArrowRight className="w-5 h-5" />
                  </button>

                  <button
                    type="button"
                    onClick={() => setQuizStep(2)}
                    className="text-xs font-semibold text-slate-400 hover:text-slate-200 flex items-center gap-1 mt-4"
                    data-testid="quiz-back-btn-2"
                  >
                    <RotateCcw className="w-3.5 h-3.5" />
                    <span>Back to previous question</span>
                  </button>
                </form>
              )}
            </div>
          </div>
        )}

        {/* FUNNEL STEP 3: CORE PAID COURSE OFFERS & INSTANT SIMULATED CHECKOUT */}
        {activeStep === 3 && (
          <div className="space-y-12 animate-fade-in" data-testid="funnel-step-offers">
            
            {/* Urgent Countdown Timer */}
            <div className="max-w-2xl mx-auto bg-gradient-to-r from-amber-500/10 via-orange-500/10 to-transparent border border-amber-500/20 rounded-2xl p-6 text-center space-y-3 relative overflow-hidden">
              <div className="absolute top-0 right-0 p-1.5 bg-amber-500 text-slate-950 text-[9px] font-black uppercase tracking-widest rounded-bl-lg">
                Exclusive Opportunity
              </div>
              <p className="text-xs sm:text-sm font-bold text-amber-400 flex items-center justify-center gap-1.5">
                <Clock className="w-4 h-4 animate-pulse" />
                <span>SPECIAL INTRODUCTORY DISCOUNTS EXPIRING SHORTLY</span>
              </p>
              
              <div className="flex justify-center items-center gap-3 pt-1">
                <div className="bg-slate-900 border border-slate-800 rounded-lg px-3 py-1.5 min-w-[50px]">
                  <span className="text-xl sm:text-2xl font-black text-slate-100">{String(timerMinutes).padStart(2, "0")}</span>
                  <span className="text-[10px] text-slate-400 block uppercase font-medium">Min</span>
                </div>
                <span className="text-xl sm:text-2xl font-black text-amber-500 animate-pulse">:</span>
                <div className="bg-slate-900 border border-slate-800 rounded-lg px-3 py-1.5 min-w-[50px]">
                  <span className="text-xl sm:text-2xl font-black text-slate-100">{String(timerSeconds).padStart(2, "0")}</span>
                  <span className="text-[10px] text-slate-400 block uppercase font-medium">Sec</span>
                </div>
              </div>
              
              <p className="text-xs text-slate-400 font-medium pt-1">
                Claim additional 15% discount using coupon <span className="text-slate-100 font-bold px-1.5 py-0.5 rounded bg-slate-900 border border-slate-850">HAPPINESS</span>
              </p>
            </div>

            <div className="text-center space-y-4 max-w-3xl mx-auto">
              <span className="text-xs font-bold text-amber-400 uppercase tracking-widest">Enroll Today</span>
              <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Structured Cognitive Curriculums</h2>
              <p className="text-slate-400 text-sm sm:text-base">
                Take the leap from introductory learning resources to deep, structured coaching programs designed and conducted by Sreenivas Murty K.
              </p>
            </div>

            {/* Paid Packages Pricing comparison */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-stretch">
              {packages.map((pkg) => (
                <div
                  key={pkg.id}
                  className={`rounded-3xl border bg-slate-900/60 p-8 flex flex-col justify-between transition-all duration-300 relative group hover:shadow-2xl ${pkg.color} ${pkg.isPopular ? "scale-102 lg:translate-y-[-8px]" : ""}`}
                  data-testid={`package-card-${pkg.id}`}
                >
                  {pkg.isPopular && (
                    <div className="absolute top-[-14px] left-1/2 transform -translate-x-1/2 px-4 py-1 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 text-slate-950 font-black text-[10px] tracking-widest uppercase shadow-md shadow-amber-500/10">
                      Best Transformation Value
                    </div>
                  )}

                  <div className="space-y-6">
                    <div className="space-y-2">
                      <span className="text-[10px] font-bold tracking-widest uppercase text-slate-400 block">
                        {pkg.badge}
                      </span>
                      <h3 className="text-xl font-bold text-slate-100 group-hover:text-amber-400 transition-colors">
                        {pkg.title}
                      </h3>
                      <p className="text-xs text-slate-400 flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5 text-slate-500" />
                        <span>{pkg.duration}</span>
                      </p>
                    </div>

                    <div className="border-y border-slate-850 py-4 flex items-baseline gap-2">
                      <span className="text-3xl sm:text-4xl font-black text-slate-100">
                        ₹{pkg.price}
                      </span>
                      <span className="text-sm text-slate-500 line-through">
                        ₹{pkg.originalPrice}
                      </span>
                      <span className="text-xs font-bold text-amber-500 uppercase tracking-widest ml-auto">
                        Save {Math.round(((pkg.originalPrice - pkg.price) / pkg.originalPrice) * 100)}%
                      </span>
                    </div>

                    <ul className="space-y-3">
                      {pkg.features.map((feat, i) => (
                        <li key={i} className="flex items-start gap-2.5 text-xs text-slate-300">
                          <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                          <span className="leading-relaxed">{feat}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="pt-8">
                    <button
                      onClick={() => {
                        setSelectedPackage(pkg);
                        setShowCheckoutModal(true);
                      }}
                      className={`w-full py-3.5 rounded-xl font-bold text-sm tracking-wide active:scale-98 transition-all flex items-center justify-center gap-2 ${pkg.isPopular ? "bg-gradient-to-r from-amber-500 to-orange-500 text-slate-950 hover:from-amber-400 hover:to-orange-400" : "bg-slate-950 border border-slate-850 hover:border-slate-700 text-slate-200"}`}
                      data-testid={`package-checkout-btn-${pkg.id}`}
                    >
                      <Lock className="w-4 h-4" />
                      <span>Unlock Instant Enrollment</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Money back and Secure Payment assurance info */}
            <div className="max-w-3xl mx-auto text-center border-t border-slate-900 pt-8 space-y-4">
              <div className="flex flex-wrap justify-center items-center gap-6 text-xs text-slate-500">
                <span className="flex items-center gap-1.5"><Lock className="w-4 h-4 text-emerald-500" /> 256-Bit SSL Encrypted Checkout</span>
                <span className="flex items-center gap-1.5"><Gift className="w-4 h-4 text-orange-500" /> 100% Happiness Money-Back Guarantee</span>
              </div>
              <p className="text-[11px] text-slate-600 max-w-xl mx-auto leading-relaxed">
                If you are not thoroughly thrilled with Sreenivas Murty K.'s cognitive techniques within the first week of live sessions, let us know for a prompt and polite 100% refund.
              </p>
            </div>
          </div>
        )}

        {/* FUNNEL STEP 4: THANK YOU / SUCCESS & CONFIRMATION HUB */}
        {activeStep === 4 && (
          <div className="max-w-2xl mx-auto py-4 sm:py-8 animate-fade-in" data-testid="funnel-step-success">
            <div className="rounded-3xl border border-slate-800 bg-slate-900/60 p-8 sm:p-10 space-y-8 text-center relative overflow-hidden">
              <div className="absolute top-0 right-0 w-[200px] h-[200px] bg-emerald-500/5 rounded-full blur-[60px] pointer-events-none" />
              
              <div className="w-16 h-16 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-500 flex items-center justify-center mx-auto shadow-lg shadow-emerald-500/5">
                <CheckCircle className="w-9 h-9 stroke-[2.5]" />
              </div>

              <div className="space-y-3">
                <span className="text-xs font-bold text-emerald-400 uppercase tracking-widest">ACCESS CONFIRMED</span>
                <h2 className="text-3xl font-extrabold tracking-tight text-slate-100">
                  Congratulations {leadName || "Cognitive Learner"}!
                </h2>
                <p className="text-sm text-slate-400 max-w-lg mx-auto">
                  You are registered successfully for Sreenivas Murty K.'s live introductory masterclass and your learning resource kit is fully generated.
                </p>
              </div>

              {/* Free Resource Starter Kit Container */}
              <div className="bg-slate-950 border border-slate-850 rounded-2xl p-6 text-left space-y-4">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-amber-500/10 rounded-lg border border-amber-500/20 shrink-0">
                    <BookOpen className="w-6 h-6 text-amber-500" />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-slate-200">Brain Sttudio Starter kit Playbook</h4>
                    <span className="text-[11px] text-slate-500 block">PDF eBook • 4.8MB • High Resolution</span>
                  </div>
                </div>

                <div className="border-t border-slate-900/60 pt-4 space-y-2">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">INCLUDED CHAPTERS:</span>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-slate-400">
                    <span className="flex items-center gap-1.5"><CheckCircle className="w-3.5 h-3.5 text-amber-500" /> 3x3 Rubik's Cube Quick solve</span>
                    <span className="flex items-center gap-1.5"><CheckCircle className="w-3.5 h-3.5 text-amber-500" /> Pyraminx Pyramid Formula</span>
                    <span className="flex items-center gap-1.5"><CheckCircle className="w-3.5 h-3.5 text-amber-500" /> 3-Ball Cascade Hand movements</span>
                    <span className="flex items-center gap-1.5"><CheckCircle className="w-3.5 h-3.5 text-amber-500" /> Finger Gym Concentration charts</span>
                  </div>
                </div>

                <button
                  onClick={() => {
                    toast.success("Downloading Sreenivas K's Brain Sttudio Starter Kit PDF Playbook...");
                  }}
                  className="w-full py-3 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-xs sm:text-sm flex items-center justify-center gap-2 transition-colors"
                  data-testid="pdf-download-button"
                >
                  <Download className="w-4 h-4" />
                  <span>Download Free PDF Playbook</span>
                </button>
              </div>

              {/* Next Interactive Action triggers */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <a
                  href="https://chat.whatsapp.com/mock-brain-sttudio"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => {
                    e.preventDefault();
                    toast.success("Simulating redirect to Sreenivas Murty K's VIP Student WhatsApp Group");
                  }}
                  className="p-4 rounded-xl bg-[#25D366]/10 border border-[#25D366]/20 text-slate-300 hover:text-white hover:bg-[#25D366]/20 transition-all text-left space-y-1.5"
                  data-testid="whatsapp-join-button"
                >
                  <span className="text-xs font-bold text-[#25D366] block">STEP 1: VIP WHATSAPP GROUP</span>
                  <p className="text-xs text-slate-400">Join our community group to receive direct video calls, timers, and class schedule updates.</p>
                </a>

                <button
                  onClick={() => {
                    toast.success("Demo Masterclass added to your Calendar (Google/iCal)");
                  }}
                  className="p-4 rounded-xl bg-cyan-500/10 border border-cyan-500/20 text-slate-300 hover:text-white hover:bg-cyan-500/20 transition-all text-left space-y-1.5"
                  data-testid="calendar-add-button"
                >
                  <span className="text-xs font-bold text-cyan-400 block">STEP 2: ADD TO CALENDAR</span>
                  <p className="text-xs text-slate-400">Add the introductory demo session of Sreenivas Murty K to ensure you do not miss it.</p>
                </button>
              </div>

              {/* Reset to home button */}
              <button
                onClick={() => {
                  setActiveStep(1);
                  setQuizStep(1);
                }}
                className="text-xs font-semibold text-slate-400 hover:text-slate-200 flex items-center gap-1.5 mx-auto pt-2"
                data-testid="success-back-home-button"
              >
                <RotateCcw className="w-4 h-4 text-slate-500" />
                <span>Go back to Homepage</span>
              </button>
            </div>
          </div>
        )}

      </main>

      {/* FOOTER */}
      <footer className="border-t border-slate-900 bg-slate-950 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-start">
            <div className="md:col-span-5 space-y-4">
              <img 
                src="https://tagmango.com/assets/bsg-logo-black-text-54297b29f2f0e6a87f838d7ace724844.png" 
                alt="Brain Sttudio Global Logo" 
                className="h-14 w-auto object-contain bg-white rounded-xl p-1.5 border border-white/20 shadow-md shadow-white/5"
              />
              <p className="text-xs text-slate-400 max-w-sm leading-relaxed">
                Founded by Sreenivas Murty K, Brain Sttudio Global (The Real Happiness Factory) conducts interactive cognitive programs globally to unleash human brain potential and mindfulness.
              </p>
              <p className="text-[10px] text-slate-500 font-medium">
                Creator ID: 6a85848db7d31750d778606f • learn.brainsttudioglobal.com
              </p>
            </div>

            <div className="md:col-span-3 space-y-3 text-xs">
              <h4 className="font-bold text-slate-200 uppercase tracking-wider">Our Cognitive Arts</h4>
              <ul className="space-y-2 text-slate-400">
                <li>Rubik’s Cube speed solving</li>
                <li>Pyraminx Pyramid methods</li>
                <li>3-Ball Neuro Juggling</li>
                <li>Finger Brain Handwriting Graphy</li>
                <li>Speed Cups Sport stacking</li>
              </ul>
            </div>

            <div className="md:col-span-4 space-y-3 text-xs">
              <h4 className="font-bold text-slate-200 uppercase tracking-wider">Contact & Legal</h4>
              <ul className="space-y-2 text-slate-400 leading-relaxed">
                <li>Email: support@brainsttudioglobal.com</li>
                <li>Head Office: Sreenivas Murty K., Bangalore, India</li>
                <li>Terms of Service • Privacy Policy • Refund Guidelines</li>
                <li className="text-[10px] text-slate-500 mt-2">
                  This landing page and interactive sales funnel is developed by Mango AI. TagMango Studio.
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-slate-900/60 pt-6 flex flex-col sm:flex-row justify-between items-center text-[11px] text-slate-500 gap-4">
            <span>© 2026 Brain Sttudio Global — The Real Happiness Factory. All Rights Reserved.</span>
            <span>Made with ❤ by Mango AI (TagMango Funnel Builder)</span>
          </div>
        </div>
      </footer>

      {/* DETAILED INTRO VIDEO MODAL */}
      {isVideoModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/90 backdrop-blur-md animate-fade-in" data-testid="video-modal-overlay">
          <div className="relative bg-slate-900 border border-slate-800 rounded-3xl w-full max-w-2xl overflow-hidden shadow-2xl p-5 space-y-4">
            <button
              onClick={() => setIsVideoModalOpen(false)}
              className="absolute top-4 right-4 p-2 rounded-lg bg-slate-950 hover:bg-slate-800 border border-slate-850 hover:border-slate-700 transition-colors"
              data-testid="video-modal-close"
            >
              <X className="w-4 h-4 text-slate-400 hover:text-white" />
            </button>

            <div className="space-y-1">
              <h3 className="text-lg font-bold text-slate-100">Welcome to Sreenivas Murty K’s Workshop</h3>
              <p className="text-xs text-slate-400">Discover how mechanical toys & hand-eye exercises stimulate rapid neuroplasticity.</p>
            </div>

            {/* Simulated interactive video screen */}
            <div className="aspect-video bg-slate-950 border border-slate-850 rounded-xl flex flex-col items-center justify-center p-6 text-center space-y-4 relative overflow-hidden group">
              <div className="absolute inset-0 opacity-10 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-amber-500 via-transparent to-transparent pointer-events-none" />
              
              <div className="w-16 h-16 rounded-full bg-amber-500/10 border border-amber-500/20 flex items-center justify-center group-hover:scale-105 duration-300 shadow-lg shadow-amber-500/5 cursor-pointer" onClick={() => toast.info("Intro video playback simulated!")}>
                <Play className="w-8 h-8 text-amber-500 fill-amber-500 translate-x-0.5" />
              </div>

              <div className="space-y-1.5 max-w-md">
                <span className="text-xs text-amber-400 font-bold uppercase tracking-widest">Sreenivas Murty K.</span>
                <p className="text-xs text-slate-400">
                  "I teach parents, children, and professionals simple coordination games that break anxiety, boost focus, and balance left-right brain activity."
                </p>
              </div>

              {/* Progress bar simulation */}
              <div className="w-full absolute bottom-0 left-0 right-0 h-1 bg-slate-800">
                <div className="h-full bg-gradient-to-r from-amber-500 to-orange-500 w-1/3" />
              </div>
            </div>

            <div className="flex justify-between items-center text-xs text-slate-400">
              <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> Demo Duration: 2 mins 45 secs</span>
              <button
                onClick={() => {
                  setIsVideoModalOpen(false);
                  setActiveStep(2);
                  setQuizStep(1);
                }}
                className="px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold transition-colors"
                data-testid="video-modal-cta"
              >
                Get Free Access Kit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SIMULATED SECURE CHECKOUT MODAL */}
      {showCheckoutModal && selectedPackage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/90 backdrop-blur-md animate-fade-in" data-testid="checkout-modal-overlay">
          <div className="relative bg-slate-900 border border-slate-800 rounded-3xl w-full max-w-md overflow-hidden shadow-2xl p-6 sm:p-8 space-y-6">
            <button
              onClick={() => setShowCheckoutModal(false)}
              className="absolute top-4 right-4 p-2 rounded-lg bg-slate-950 hover:bg-slate-800 border border-slate-850 hover:border-slate-700 transition-colors"
              data-testid="checkout-modal-close"
            >
              <X className="w-4 h-4 text-slate-400 hover:text-white" />
            </button>

            <div className="space-y-1">
              <span className="text-[10px] font-bold tracking-widest uppercase text-amber-500">Secure simulated Checkout</span>
              <h3 className="text-xl font-bold text-slate-100">Unlock Program Access</h3>
              <p className="text-xs text-slate-400">You are enrolling in: <span className="text-slate-200 font-medium">{selectedPackage.title}</span></p>
            </div>

            {/* Price anchoring summary with discount code field */}
            <div className="bg-slate-950 border border-slate-850 rounded-xl p-4 space-y-3">
              <div className="flex justify-between text-xs text-slate-400">
                <span>Standard Price:</span>
                <span className="line-through">₹{selectedPackage.originalPrice}</span>
              </div>
              <div className="flex justify-between text-xs text-slate-400">
                <span>Special Fast-Action Offer:</span>
                <span className="text-slate-100">₹{selectedPackage.price}</span>
              </div>

              {discountPercent > 0 && (
                <div className="flex justify-between text-xs text-emerald-400 font-bold">
                  <span>Coupon Discount ({appliedCouponName} 15%):</span>
                  <span>-₹{Math.round(selectedPackage.price * 0.15)}</span>
                </div>
              )}

              <div className="border-t border-slate-900/60 pt-3 flex justify-between text-sm font-bold text-slate-100">
                <span>Due Today:</span>
                <span className="text-amber-500">₹{Math.round(selectedPackage.price * (1 - discountPercent / 100))}</span>
              </div>
            </div>

            {/* Coupon Code Input */}
            <div className="space-y-1.5">
              <label htmlFor="coupon-input" className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Have a Coupon?</label>
              <div className="flex gap-2">
                <div className="relative flex-grow">
                  <Percent className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-600" />
                  <input
                    id="coupon-input"
                    type="text"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value)}
                    placeholder="Enter code: HAPPINESS"
                    className="w-full pl-9 pr-4 py-2.5 rounded-lg bg-slate-950 border border-slate-800 text-slate-100 focus:outline-none focus:border-amber-500 text-xs"
                    data-testid="coupon-code-input"
                  />
                </div>
                <button
                  type="button"
                  onClick={handleApplyCoupon}
                  className="px-4 py-2.5 rounded-lg bg-slate-800 border border-slate-700 hover:border-slate-650 hover:bg-slate-750 text-slate-200 text-xs font-bold transition-all"
                  data-testid="apply-coupon-button"
                >
                  Apply
                </button>
              </div>
            </div>

            {/* Payments Selector tab option */}
            <div className="space-y-2">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Payment Method</span>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <button
                  type="button"
                  onClick={() => setPaymentMethod("upi")}
                  className={`py-2 px-3 rounded-lg border text-center font-bold transition-all ${paymentMethod === "upi" ? "bg-amber-500/10 border-amber-500 text-amber-500" : "bg-slate-950 border-slate-850 text-slate-400 hover:border-slate-800"}`}
                  data-testid="checkout-payment-upi-tab"
                >
                  UPI ID (Simulated)
                </button>
                <button
                  type="button"
                  onClick={() => setPaymentMethod("card")}
                  className={`py-2 px-3 rounded-lg border text-center font-bold transition-all ${paymentMethod === "card" ? "bg-amber-500/10 border-amber-500 text-amber-500" : "bg-slate-950 border-slate-850 text-slate-400 hover:border-slate-800"}`}
                  data-testid="checkout-payment-card-tab"
                >
                  Credit/Debit Card
                </button>
              </div>
            </div>

            {/* Payment Specific Input Fields */}
            {paymentMethod === "upi" ? (
              <div className="space-y-1.5">
                <label htmlFor="upi-input" className="text-xs font-semibold text-slate-300 block">Simulated UPI Address</label>
                <input
                  id="upi-input"
                  type="text"
                  required
                  value={simulatedUpiId}
                  onChange={(e) => setSimulatedUpiId(e.target.value)}
                  placeholder="success@upi"
                  className="w-full px-4 py-3 rounded-lg bg-slate-950 border border-slate-800 text-slate-100 focus:outline-none focus:border-amber-500 text-xs"
                  data-testid="checkout-upi-input"
                />
              </div>
            ) : (
              <div className="space-y-1.5">
                <label htmlFor="card-input" className="text-xs font-semibold text-slate-300 block">Simulated Card Number</label>
                <input
                  id="card-input"
                  type="text"
                  required
                  value={simulatedCardNumber}
                  onChange={(e) => setSimulatedCardNumber(e.target.value)}
                  placeholder="4111 2222 3333 4444"
                  className="w-full px-4 py-3 rounded-lg bg-slate-950 border border-slate-800 text-slate-100 focus:outline-none focus:border-amber-500 text-xs"
                  data-testid="checkout-card-input"
                />
              </div>
            )}

            <button
              onClick={handleCompleteCheckout}
              className="w-full py-3.5 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-slate-950 font-bold text-sm hover:from-amber-400 hover:to-orange-400 active:scale-98 shadow-lg shadow-amber-500/5 transition-all flex items-center justify-center gap-1.5"
              data-testid="checkout-submit-button"
            >
              <Lock className="w-4 h-4" />
              <span>Simulate Secure Payment</span>
            </button>
          </div>
        </div>
      )}

      {/* MANGO AI CREATOR ANALYTICS SIDEBAR / MODAL OVERLAY */}
      {showDashboard && (
        <>
          {/* Backdrop Blur Overlay */}
          <div 
            className="fixed inset-0 bg-slate-950/60 backdrop-blur-sm z-40 transition-opacity duration-300"
            onClick={() => setShowDashboard(false)}
            data-testid="analytics-backdrop-overlay"
          />
          <div className="fixed inset-y-0 right-0 z-50 w-full max-w-lg bg-slate-950 border-l border-slate-850 shadow-2xl p-6 overflow-y-auto animate-slide-left" data-testid="analytics-sidebar-overlay">
          <div className="flex justify-between items-center border-b border-slate-900 pb-4 mb-6">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-cyan-400 animate-pulse" />
              <div>
                <h3 className="font-extrabold text-slate-100 text-base">Mango Studio Funnel Stats</h3>
                <span className="text-[10px] text-slate-500 font-semibold block uppercase">Tracking: learn.brainsttudioglobal.com</span>
              </div>
            </div>
            
            <button
              onClick={() => setShowDashboard(false)}
              className="p-1.5 rounded-lg bg-slate-900 hover:bg-slate-850 border border-slate-800 text-slate-400 hover:text-white transition-colors"
              data-testid="analytics-sidebar-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="space-y-6">
            
            {/* Lead Stats Block Grid */}
            <div className="grid grid-cols-3 gap-3">
              <div className="p-3 bg-slate-900/60 rounded-xl border border-slate-850">
                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest block">Visits</span>
                <span className="text-xl font-extrabold text-slate-100 block mt-0.5">{stats.visits}</span>
              </div>
              <div className="p-3 bg-slate-900/60 rounded-xl border border-slate-850">
                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest block">Leads</span>
                <span className="text-xl font-extrabold text-amber-500 block mt-0.5">{stats.leadsCount}</span>
              </div>
              <div className="p-3 bg-slate-900/60 rounded-xl border border-slate-850">
                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest block">Sales</span>
                <span className="text-xl font-extrabold text-emerald-500 block mt-0.5">{stats.purchasesCount}</span>
              </div>
            </div>

            {/* Financial Status and Conversion */}
            <div className="grid grid-cols-2 gap-3">
              <div className="p-4 bg-slate-900/60 rounded-xl border border-slate-850 text-left">
                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest block">Visits to Lead Rate</span>
                <span className="text-lg font-black text-cyan-400 block mt-0.5">{calcLeadsRate}%</span>
                <span className="text-[9px] text-slate-500 block">Industry avg: 10-15%</span>
              </div>
              <div className="p-4 bg-slate-900/60 rounded-xl border border-slate-850 text-left">
                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest block">Lead to Purchase Rate</span>
                <span className="text-lg font-black text-purple-400 block mt-0.5">{calcPurchaseRate}%</span>
                <span className="text-[9px] text-slate-500 block">Industry avg: 15-25%</span>
              </div>
            </div>

            {/* Simulated Live Funnel Progress block (reusable visual conversion pipeline) */}
            <div className="p-4 bg-slate-900/60 rounded-xl border border-slate-850 space-y-3">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">Simulated Funnel Pipeline Conversion</span>
              
              <div className="space-y-2">
                <div className="space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="font-semibold text-slate-300">1. Landing Page Visits</span>
                    <span className="font-black text-slate-200">{stats.visits} (100%)</span>
                  </div>
                  <div className="w-full bg-slate-950 rounded-full h-2">
                    <div className="h-full bg-cyan-400 rounded-full" style={{ width: "100%" }} />
                  </div>
                </div>

                <div className="space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="font-semibold text-slate-300">2. Assessment Registrations</span>
                    <span className="font-black text-amber-500">{stats.leadsCount} ({calcLeadsRate}%)</span>
                  </div>
                  <div className="w-full bg-slate-950 rounded-full h-2">
                    <div className="h-full bg-amber-500 rounded-full" style={{ width: `${Math.min(100, Number(calcLeadsRate))}%` }} />
                  </div>
                </div>

                <div className="space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="font-semibold text-slate-300">3. Paid Course Enrollments</span>
                    <span className="font-black text-emerald-500">{stats.purchasesCount} ({calcOverallRate}%)</span>
                  </div>
                  <div className="w-full bg-slate-950 rounded-full h-2">
                    <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${Math.min(100, Number(calcOverallRate))}%` }} />
                  </div>
                </div>
              </div>
            </div>

            {/* Leads List Viewer */}
            <div className="space-y-2 text-left">
              <div className="flex justify-between items-center">
                <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Leads Log ({leads.length})</h4>
                <div className="flex gap-2">
                  <button
                    onClick={handleResetData}
                    className="p-1.5 rounded bg-slate-900 border border-slate-850 text-slate-400 hover:text-rose-500 transition-colors"
                    title="Reset stats & leads"
                    data-testid="analytics-reset-btn"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={handleAddMockLeads}
                    className="p-1.5 rounded bg-slate-900 border border-slate-850 text-slate-400 hover:text-cyan-400 transition-colors"
                    title="Seed mock statistical history"
                    data-testid="analytics-seed-btn"
                  >
                    <Plus className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>

              <div className="max-h-60 overflow-y-auto border border-slate-850 rounded-xl bg-slate-950 text-xs divide-y divide-slate-900">
                {leads.length === 0 ? (
                  <p className="p-4 text-center text-slate-600">No leads recorded yet. Go through the funnel steps to add yourself!</p>
                ) : (
                  leads.map((lead) => (
                    <div key={lead.id} className="p-3 space-y-1 hover:bg-slate-900/40 transition-colors" data-testid={`lead-log-item-${lead.id}`}>
                      <div className="flex justify-between items-center">
                        <span className="font-bold text-slate-200">{lead.name}</span>
                        <span className={`px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider ${lead.purchased ? "bg-emerald-500/10 border border-emerald-500/20 text-emerald-400" : "bg-slate-900 border border-slate-850 text-slate-400"}`}>
                          {lead.purchased ? "Buyer" : "Lead"}
                        </span>
                      </div>
                      <div className="flex justify-between text-[10px] text-slate-500">
                        <span>WhatsApp: {lead.whatsapp}</span>
                        <span>{lead.date}</span>
                      </div>
                      <div className="text-[10px] text-slate-400 flex flex-wrap gap-2">
                        <span className="bg-slate-900 px-1.5 py-0.5 rounded text-slate-500">Audience: {lead.audience}</span>
                        <span className="bg-slate-900 px-1.5 py-0.5 rounded text-slate-500">Focus: {lead.goals}</span>
                      </div>
                      {lead.purchased && (
                        <div className="text-[10px] text-amber-500/90 font-medium">
                          Purchased: {lead.packageName} (₹{lead.amount})
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Email and WhatsApp Followup Logs Visualiser */}
            <div className="p-4 bg-slate-900/60 rounded-xl border border-slate-850 text-left space-y-3">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">Automated Campaign Logs (Simulated)</span>
              
              <div className="space-y-2 text-[11px] text-slate-400">
                <div className="flex items-start gap-2.5">
                  <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                  <div>
                    <span className="font-bold text-slate-200 block">Registration Confirmation (Instant)</span>
                    <span className="text-slate-500">WhatsApp and email containing access codes and playbooks sent immediately.</span>
                  </div>
                </div>

                <div className="flex items-start gap-2.5">
                  <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                  <div>
                    <span className="font-bold text-slate-200 block">Learning Reminder (24 Hours)</span>
                    <span className="text-slate-500">Scheduled: WhatsApp checking-in on PDF playbook chapters & 3x3 Rubik solving algorithms.</span>
                  </div>
                </div>

                <div className="flex items-start gap-2.5">
                  <Clock className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                  <div>
                    <span className="font-bold text-slate-200 block">Limited Discount Call-out (48 Hours)</span>
                    <span className="text-slate-500">Scheduled for non-buyers: Friendly WhatsApp regarding final call for introductory price slots.</span>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </>
      )}
    </div>
  );
}
